import React, { createContext, useState, useContext, useCallback, useMemo,useEffect } from 'react';
import { useAuth } from './AuthContext'; // Assuming you have an AuthContext
import { db } from '../Firebase/firebase'; // Assuming firebase.js exports your Firestore instance
import { collection, query, where, onSnapshot, getDocs } from 'firebase/firestore';

const ChatContext = createContext(null); // Initialize with null

export const useChat = () => useContext(ChatContext);

export const ChatProvider = ({ children }) => {
  // State: 'closed', 'launcher', 'chatting'
  const [chatState, setChatState] = useState('closed');
  const [activeChatUser, setActiveChatUser] = useState(null); // Store { userId, name, profilePicUrl }
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser?.uid) {
        setTotalUnreadCount(0); // Reset if user logs out
        return; // Exit if no user
    }

    // --- Firestore Listener Logic ---
    // This is HIGHLY dependent on your DB structure.
    // Example: Querying chats where the user is a participant
    const chatsRef = collection(db, 'chats');
    const q = query(chatsRef, where('participants', 'array-contains', currentUser.uid));

    const unsubscribeChats = onSnapshot(q, (querySnapshot) => {
        let unreadPromises = []; // To collect promises for fetching unread counts per chat

        querySnapshot.forEach((chatDoc) => {
            // For each chat the user is in, check for unread messages
            // OPTION A: If chatDoc has unreadCount field per user
            // const unreadData = chatDoc.data()?.unreadCounts?.[currentUser.uid];
            // if (unreadData) { /* add to total */ }

            // OPTION B: Query messages subcollection (potentially less efficient for many chats)
            const messagesRef = collection(db, 'chats', chatDoc.id, 'messages');
            const unreadQuery = query(messagesRef,
                where('read', '==', false),
                where('receiverId', '==', currentUser.uid) // Or senderId !== currentUser.uid depending on logic
            );

            // Add promise to fetch count for this chat
             // Using getDocs here for simplicity, but could setup more listeners
            unreadPromises.push(getDocs(unreadQuery).then(msgSnap => msgSnap.size));

        });

         // Wait for all counts to be fetched and sum them up
        Promise.all(unreadPromises).then(counts => {
            const total = counts.reduce((sum, count) => sum + count, 0);
            console.log("Updating total unread count:", total); // For debugging
            setTotalUnreadCount(total);
        }).catch(error => {
            console.error("Error calculating unread counts:", error);
            // Maybe set count to 0 or handle error appropriately
            setTotalUnreadCount(0);
        });


    }, (error) => {
        console.error("Error listening to user's chats:", error);
        setTotalUnreadCount(0); // Reset on error
    });

    // Cleanup function
    return () => {
        console.log("Unsubscribing from chat listeners"); // For debugging
        unsubscribeChats();
        // Unsubscribe from any other listeners setup inside
    };

}, [currentUser?.uid]); // Re-run effect if user changes

  // --- Helper functions ---
  const getChatId = useCallback((userId1, userId2) => {
    if (!userId1 || !userId2) return null;
    return [userId1, userId2].sort().join('_');
  }, []);

  const calculatedChatId = useMemo(() => {
      if (currentUser?.uid && activeChatUser?.userId) {
          return getChatId(currentUser.uid, activeChatUser.userId);
      }
      return null;
  }, [currentUser?.uid, activeChatUser?.userId, getChatId]);


  // --- Action functions ---
  const openChatLauncher = useCallback(() => {
    if (!currentUser) return; // Need user logged in
    setChatState('launcher');
    setActiveChatUser(null); // Ensure no chat is active when opening launcher
  }, [currentUser]);

  const openChatWithUser = useCallback((user) => {
    if (!currentUser || !user || !user.userId) return;
    setActiveChatUser(user); // Set the target user
    setChatState('chatting'); // Set state to show chat view
  }, [currentUser]);

  const closeChatWindow = useCallback(() => {
    setChatState('closed');
    setActiveChatUser(null); // Clear active user
  }, []);

  // Function to go back from chat view to launcher view (optional)
  const returnToLauncher = useCallback(() => {
      if (chatState === 'chatting') {
          setActiveChatUser(null);
          setChatState('launcher');
      }
  }, [chatState]);


  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    chatState, // 'closed', 'launcher', 'chatting'
    activeChatUser,
    activeChatId: calculatedChatId, // Use the memoized chatId
    currentUserConnections: currentUser?.connections || [], // Pass connections if available in AuthContext
    openChatLauncher,
    openChatWithUser,
    closeChatWindow,
    returnToLauncher, // Add the back function
    getChatId, // Keep getChatId if needed elsewhere directly
    totalUnreadCount, // <-- Add the state here
  }), [
      chatState,
      activeChatUser,
      calculatedChatId,
      currentUser?.connections, // Add dependency if passing connections
      openChatLauncher,
      openChatWithUser,
      closeChatWindow,
      returnToLauncher,
      getChatId,
      totalUnreadCount, // <-- Add dependency here

  ]);

  // Only provide context if a user is logged in
  return (
    <ChatContext.Provider value={currentUser ? value : null}>
      {children}
    </ChatContext.Provider>
  );
};